import { Injectable } from '@angular/core';
import { Company, Label, Location, LocationGroup, MaintenanceType, Pendency, Problem, Segment, User } from '@core/interfaces';
import { TranslateService } from '@ngx-translate/core';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';


export interface MaintenanceFilterData {
  segments: Segment[];
  problems: Problem[];
  labels: Label[];
  locationsGroups: LocationGroup[];
  companies: Company[];
  maintenanceTypes: MaintenanceType[];
  assignees: User[];
  requesters: User[];
  pendencies: Pendency[];
}

@Injectable({
  providedIn: 'root'
})
export class MaintenanceFilterApi {
  constructor(
    private readonly apollo: Apollo,
    private translateService: TranslateService
  ) {}

  public async getFilterData(): Promise<MaintenanceFilterData> {
    const query = gql`
      query getMaintenanceFiltersData {
        assignees: users(limit: 1000, orderBy: name_asc, where: { archived: false, type_in: [all,admin] }) {
          items {
            id
            name
          }
        }
        requesters: requesters(limit: 1000, orderBy: name_asc, where: { archived: false, type_in: [all,requester,admin] }) {
          items {
            id
            name
          }
        }
        segments (limit: 100, orderBy: name_asc, where: { archived: false }) {
          items {
            id
            name
          }
        }
        maintenanceTypes (limit: 100, orderBy: name_asc, where: { archived: false }) {
          items {
            id
            name
          }
        }
        labels (limit: 100, orderBy: { column: name, direction: asc}, where: { type: maintenance}) {
          items {
            id
            name
            color
          }
        }
        locationsGroups (limit: 300, orderBy: name_asc, where: { archived: false }) {
          items {
            id
            name
          }
        }
        companies (limit: 500, orderBy: name_asc,  where: { archived: false }) {
          items {
            id
            name
          }
        }
        problems(orderBy: { column: name, direction: asc}, limit: 100,  where: { archived: false }) {
          items {
            id
            name
          }
        },
        pendencies (orderBy: createdAt_asc, take: 100,  where: { archived: false }) {
          items {
            id
            title
            archived
            status
            treatmentStatus
            pendencyType {
              name
            }
          }
        }
      }
    `;

    const { data } = await this.apollo.query<any>({ query }).toPromise();

    const labels = [
      {
        id: null,
        name: this.translateService.instant('MAINTENANCES.FILTERS.NO_LABELS'),
      },
      ...data.labels.items
    ];

    return {
      assignees: data.assignees.items,
      requesters: data.requesters.items,
      segments: data.segments.items,
      problems: data.problems.items,
      labels: labels,
      locationsGroups: data.locationsGroups.items,
      companies: data.companies.items,
      maintenanceTypes: data.maintenanceTypes.items,
      pendencies: data.pendencies.items
    };
  }

  public async findLocations(locationIds: string[]): Promise<Location[]> {
    const query = gql`query searchLocations($where: LocationWhereInput, $limit: Int, $offset: Int) {
      locations(
        where: $where
        orderBy: {
          column: name,
          direction: asc
        }
        limit: $limit
        offset: $offset
      ){
        items {
          id
          name
          address {
            formattedAddress,
            postalCode,
            streetName,
            city,
            number,
            complement,
            neighborhood,
            state,
            coords {
              latitude,
              longitude
            }
          }
        }
      }
    }`;

    const result = await this.apollo.query<any>({
      query,
      variables: {
        where: {
          id_in: locationIds,
        },
        offset: 0,
        limit: locationIds.length,
      }
    }).toPromise();

    return result.data.locations.items;
  }
}

